import React, { useState } from 'react';
import { Users, Lightbulb, Shield, Rocket, BookOpen, FlaskConical, Sprout, Heart, Building2, Globe, Award, TrendingUp, ChevronRight, UserCircle } from 'lucide-react';
import Header from "../components/Header";
import ObjectivesAccordion from "../components/aicoe/Objectives";
import ManagementHierarchy from "../components/aicoe/ManagementTeam";
import PartnershipNetwork from "../components/aicoe/PartnershipNetwork";
import Footer from "../components/footer";
import { IntroductionSection, StrategicChallenges, ThematicPriorities } from "../components/aicoe/AICIE";
import {
    OfficialLaunchSection,
    AICDNLaunchSection,
    OrganizationalRolesSection,
    AICDNCooperationSection,
    StrategicObjectivesSection,
    ConclusionSection
} from "../components/aicoe/AICDNSection";

import { Helmet } from "react-helmet";

export default function AICoEShowcase() {
    const [expandedGov, setExpandedGov] = useState(null);

    const stats = [
        { label: 'Training Capacity', value: '200+', icon: Users },
        { label: 'Office Spaces', value: '7', icon: Building2 },
        { label: 'Accommodation', value: '70 pax', icon: Building2 },
        { label: 'Active Projects', value: '4+', icon: Rocket }
    ];



    const functions = [
        {
            title: 'Education & Training',
            icon: BookOpen,
            items: ['Postgraduate (MSc/PhD) AI research', 'Masterclasses and executive training', 'Certificate bootcamps', 'Webinars & Workshops']
        },
        {
            title: 'Applied Research & Labs',
            icon: FlaskConical,
            items: ['Health AI, Agri-AI, Urban AI, Climate AI', 'Small-model & low-resource AI research']
        },
        {
            title: 'Pilots & Field Incubation',
            icon: Sprout,
            items: ['Rapid prototyping & user-centered design', 'Ethically sourced datasets']
        },
        {
            title: 'Policy & Ethics',
            icon: Shield,
            items: ['Policy briefs & regulatory playbooks', 'Responsible AI audits']
        },
        {
            title: 'Innovation & Commercialization',
            icon: Lightbulb,
            items: ['Incubator for spinouts', 'Business model support']
        }
    ];

    const steeringBoard = [
        {
            category: 'Host Institution',
            members: ['Jomo Kenyatta University of Agriculture and Technology (JKUAT)']
        },
        {
            category: 'Universities & Research Institutions',
            members: [
                'Jomo Kenyatta University of Agriculture and Technology (JKUAT) - Director School of Computing and Information Technology',
                'Pan African University Institute of Basic Sciences, Technology and Innovation PAUSTI - Electrical Engineering Programme Coordinator',
                'African Institute for Capacity Development (AICAD)'
            ]
        },
        {
            category: 'Public Sector',
            members: [
                'The Office of the Special Envoy on Technology',
                'Kenya Open Government Partnership',
                'Ministries of Agriculture KALRO',
                'Ministry of Health',
                'Ministry of Education',
                'Ministry of Information, Communications and The Digital Economy (MICDE)'
            ]
        },
        {
            category: 'Private Sector',
            members: ['Data Trace International Ltd']
        },
        {
            category: 'NGO/CSO',
            members: ['CODATA', 'ActionLab']
        },
        {
            category: 'Development Partners',
            members: ['Africa Open Science Platform (AOSP)']
        }
    ];

    const managementTeam = [
        { role: 'Lead Scientist, AOSP EA Node', name: 'Prof Muliaro Wafula' },
        { role: 'Coordinator, Research & Capacity Building', name: 'Dr Agnes Mindila' },
        { role: 'Coordinator, Operations', name: 'JKUAT Officer' },
        { role: 'Coordinator, Development, Innovation and Partnerships', name: 'Alex Musyoki' },
        { role: 'Expert, AI Model Development', name: 'Brian Macharia' },
        { role: 'Expert, Data Science', name: 'Maurice Njoroge' },
        { role: 'Expert, Infrastructure Federation', name: 'Arthur Wainaina' },
        { role: 'Expert, Data Engineer', name: 'Kevin Miguta' },
        { role: 'Expert, Systems Integration and Security', name: 'Brian Ng\'eno' },
        { role: 'Expert, Smart Learning', name: 'George Mwangi' },
        { role: 'Expert, System Design and Commercialization', name: 'Joan Ajiambo' }
    ];

    const projects = [
        {
            name: 'Kenya Ministry of Health',
            period: '2024-present',
            desc: 'Data integration & AI-powered dashboards for National Vaccine and Immunization Program',
            status: 'Deployed & Operating'
        },
        {
            name: 'APHRC DASSA Platform',
            period: '2024-2026',
            desc: 'Trusted Research Environment for African researchers with AI health models',
            status: 'Multi-Country Deployment'
        },
        {
            name: 'Decision Intelligence Fellowship',
            period: '2025-2026',
            desc: 'Mentorship program across Agriculture, Health, Labour sectors',
            status: 'Ongoing'
        },
        {
            name: 'VizAfrica Summer School',
            period: '2025',
            desc: 'Six cohorts covering AI, Data Sovereignty, GeoAI, and more',
            status: 'Planned'
        }
    ];

    return (
        <div className="min-h-screen bg-white">
            <Helmet>
                <title>Artificial Intelligence Centre of Excellence Africa | AICEX-Africa</title>

                <meta
                    name="description"
                    content="AICEX-Africa strengthens Africa’s capacity to design, deploy, govern, and benefit from Artificial Intelligence. Explore initiatives, research, policy support, and capacity-building programs accelerating AI adoption across the African continent."
                />

                <meta
                    name="keywords"
                    content="Artificial Intelligence Africa, AICoE Africa, AICEX-Africa, AICEX Africa, AI research Africa, AI capacity building Africa, AI governance Africa, African AI initiatives, Agentic AI Africa, AI policy Africa"
                />

                <meta property="og:type" content="website" />
                <meta property="og:site_name" content="AICEX-Africa" />

                <meta property="og:title" content="Artificial Intelligence Centre of Excellence Africa | AICEX-Africa" />
                <meta
                    property="og:description"
                    content="AICEX-Africa builds the capacity for sustainable, inclusive AI development across the African continent through research, governance frameworks, and innovation."
                />
                <meta property="og:image" content="https://aospea.org/aosp-web-logo.png" />
                <meta property="og:url" content="https://aospea.org/ai-coe" />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title"
                    content="Artificial Intelligence Centre of Excellence Africa | AICEX-Africa" />
                <meta
                    name="twitter:description"
                    content="Strengthening Africa’s AI capacity through innovation, policy support, and knowledge development."
                />
                <meta name="twitter:image" content="https://aospea.org/aosp-web-logo.png" />

                <link rel="canonical" href="https://aospea.org/ai-coe" />

                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify({
                            "@context": "https://schema.org",
                            "@type": "Organization",
                            name: "Artificial Intelligence Centre of Excellence Africa (AICEX-Africa)",
                            alternateName: "AICEX-Africa",
                            url: "https://aospea.org/ai-coe",
                            logo: "https://aospea.org/aosp-web-logo.png",
                            foundingDate: "2025",
                            location: {
                                "@type": "Place",
                                name: "AICAD",
                                address: {
                                    "@type": "PostalAddress",
                                    streetAddress: "Juja, Nairobi",
                                    addressLocality: "Nairobi",
                                    addressCountry: "Kenya",
                                },
                            },
                            image: "https://aospea.org/aosp-web-logo.png",
                            description:
                                "AICEX-Africa strengthens Africa’s capacity to design, deploy, govern, and benefit from Artificial Intelligence through research, innovation, and policy support",

                        }),
                    }}
                />

            </Helmet>

            <Header />
            {/* Hero Section */}
            <div className="relative mt-20 overflow-hidden bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800">
                <div
                    className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJyZ2JhKDI1NSwgMjU1LCAyNTUsIDAuMSkiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-30"></div>

                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-32">
                    <div className="text-center space-y-8">

                        <h1 className="text-5xl md:text-7xl font-bold text-white leading-tight">
                            AI Centre of Excellence
                            <span className="block text-white mt-2">for Capacity Building</span>
                        </h1>

                        <p className="text-xl text-white max-w-3xl mx-auto leading-relaxed">
                            Building sustainable local capacity to design, deploy and govern AI for equitable
                            development across Africa
                        </p>

                        {/*<div className="flex flex-wrap justify-center gap-4 pt-4">*/}
                        {/*    <button className="bg-white text-blue-700 px-8 py-3 rounded-full font-semibold hover:shadow-lg hover:scale-105 transition-all duration-300">*/}
                        {/*        Explore Programs*/}
                        {/*    </button>*/}
                        {/*    <button className="bg-white/10 backdrop-blur-sm border border-white/30 text-white px-8 py-3 rounded-full font-semibold hover:bg-white/20 transition-all duration-300">*/}
                        {/*        View Projects*/}
                        {/*    </button>*/}
                        {/*</div>*/}
                    </div>

                    {/* Stats Grid */}
                    {/*<div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-20">*/}
                    {/*    {stats.map((stat, idx) => (*/}
                    {/*        <div key={idx} className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 hover:bg-white/20 transition-all duration-300">*/}
                    {/*            <stat.icon className="w-8 h-8 text-purple-200 mb-3" />*/}
                    {/*            <div className="text-3xl font-bold text-white mb-1">{stat.value}</div>*/}
                    {/*            <div className="text-sm text-purple-100">{stat.label}</div>*/}
                    {/*        </div>*/}
                    {/*    ))}*/}
                    {/*</div>*/}

                </div>
            </div>

            <IntroductionSection />

            {/* Official Launch — VC Report §1 */}
            <OfficialLaunchSection />

            {/* Mission & Vision */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                <h2 className="text-4xl font-bold text-gray-900 mb-4 leading-tight">
                    Vision and Mission
                </h2>
                <div className="grid md:grid-cols-2 gap-8">
                    <div
                        className="bg-white border border-purple-200 rounded-3xl p-8 hover:shadow-xl transition-all duration-300">
                        <h3 className="text-2xl font-bold text-gray-900 mb-4">Vision</h3>
                        <p className="text-gray-700 text-lg leading-relaxed">
                            An inclusive African AI Centre of Excellence that builds sustainable local capacity to
                            design, deploy and govern AI for equitable development.
                        </p>
                    </div>

                    <div
                        className="bg-white border border-indigo-200 rounded-3xl p-8 hover:shadow-xl transition-all duration-300">
                        <h3 className="text-2xl font-bold text-gray-900 mb-4">Mission</h3>
                        <p className="text-gray-700 text-lg leading-relaxed">
                            Train talent, conduct applied research, pilot context-appropriate AI solutions, and
                            translate evidence into policy for UN Sustainable Development Goals.
                        </p>
                    </div>
                </div>
            </div>

            {/* Objectives */}
            <ObjectivesAccordion />

            {/* Strategic Objectives — VC Report §3 (8-objective grid) */}
            <StrategicObjectivesSection />

            <ThematicPriorities />

            <StrategicChallenges />

            {/* Organizational Roles — VC Report §2 */}
            <OrganizationalRolesSection />

            {/* AICDN Geneva Launch — VC Report §4 */}
            <AICDNLaunchSection />

            {/* AICDN Cooperation Areas & Key Activities — VC Report §4 sub-sections */}
            <AICDNCooperationSection />

            {/* Governance Structure */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                <h2 className="text-4xl font-bold text-gray-900 text-center mb-4">Governance Structure</h2>
                <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
                    Multi-stakeholder leadership ensuring inclusive and effective governance
                </p>

                {/* Steering Board */}
                <div className="mb-12">
                    <div className="bg-gradient-to-r from-blue-600 to-blue-600 rounded-2xl p-8 mb-6">
                        <h3 className="text-2xl font-bold text-white mb-2 flex items-center gap-3">
                            <Users className="w-8 h-8" />
                            Steering / Advisory Board
                        </h3>
                        <p className="text-purple-100">Strategic oversight and policy guidance</p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                        {steeringBoard.map((section, idx) => (
                            <div key={idx} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all duration-300">
                                <h4 className="text-lg font-bold text-blue-700 mb-4 flex items-center gap-2">
                                    <ChevronRight className="w-5 h-5" />
                                    {section.category}
                                </h4>
                                <ul className="space-y-2">
                                    {section.members.map((member, i) => (
                                        <li key={i} className="text-sm text-gray-700 flex items-start gap-2 pl-2">
                                            <span className="text-blue-500 mt-1">•</span>
                                            <span>{member}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Management Team */}
                {/*<div>*/}
                {/*    <div className="bg-gradient-to-r from-blue-600 to-blue-600 rounded-2xl p-8 mb-6">*/}
                {/*        <h3 className="text-2xl font-bold text-white mb-2 flex items-center gap-3">*/}
                {/*            <UserCircle className="w-8 h-8" />*/}
                {/*            Management Team*/}
                {/*        </h3>*/}
                {/*        <p className="text-indigo-100">Operational leadership and technical expertise</p>*/}
                {/*    </div>*/}

                {/*    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">*/}
                {/*        {managementTeam.map((member, idx) => (*/}
                {/*            <div key={idx} className="bg-gradient-to-br from-gray-50 to-purple-50 border border-purple-100 rounded-xl p-5 hover:shadow-md transition-all duration-300">*/}
                {/*                <div className="flex items-start gap-3">*/}
                {/*                    <div className="bg-blue-600 rounded-full w-10 h-10 flex items-center justify-center flex-shrink-0 mt-1">*/}
                {/*                        <UserCircle className="w-6 h-6 text-white" />*/}
                {/*                    </div>*/}
                {/*                    <div>*/}
                {/*                        <h5 className="font-semibold text-gray-900 text-sm mb-1">{member.name}</h5>*/}
                {/*                        <p className="text-xs text-gray-600 leading-relaxed">{member.role}</p>*/}
                {/*                    </div>*/}
                {/*                </div>*/}
                {/*            </div>*/}
                {/*        ))}*/}
                {/*    </div>*/}
                {/*</div>*/}
                <ManagementHierarchy />
            </div>

            {/* Functions */}
            <div className="bg-gray-50 py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h2 className="text-4xl font-bold text-gray-900 text-center mb-4">Core Functions</h2>
                    <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
                        Comprehensive services across education, research, and innovation
                    </p>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {functions.map((func, idx) => (
                            <div key={idx} className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-all duration-300">
                                <func.icon className="w-10 h-10 text-blue-600 mb-4" />
                                <h4 className="text-xl font-bold text-gray-900 mb-4">{func.title}</h4>
                                <ul className="space-y-2">
                                    {func.items.map((item, i) => (
                                        <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                                            <span className="text-blue-600 mt-1">•</span>
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Projects */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                <h2 className="text-4xl font-bold text-gray-900 text-center mb-4">Active Projects</h2>
                <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
                    Real-world AI implementations making impact across sectors
                </p>

                <div className="grid md:grid-cols-2 gap-6">
                    {projects.map((project, idx) => (
                        <div key={idx} className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-xl transition-all duration-300">
                            <div className="flex items-start justify-between mb-4">
                                <div>
                                    <h4 className="text-xl font-bold text-gray-900 mb-1">{project.name}</h4>
                                    <p className="text-sm text-gray-500">{project.period}</p>
                                </div>
                                <span className="bg-green-100 border border-green-300 text-green-700 text-xs px-3 py-1 rounded-full font-medium whitespace-nowrap">
                                    {project.status}
                                </span>
                            </div>
                            <p className="text-gray-700 leading-relaxed">{project.desc}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Facilities */}
            <div className="bg-gray-50 py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h2 className="text-4xl font-bold text-gray-900 text-center mb-4">World-Class Facilities</h2>
                    <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
                        State-of-the-art infrastructure at AICAD campus
                    </p>

                    <div className="grid md:grid-cols-3 gap-6">
                        <div className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-lg transition-all duration-300">
                            <Building2 className="w-10 h-10 text-blue-600 mb-4" />
                            <h4 className="text-xl font-bold text-gray-900 mb-4">Training Facilities</h4>
                            <ul className="space-y-2 text-gray-700 text-sm">
                                <li>• Assembly Hall (250 capacity)</li>
                                <li>• Multiple Seminar Rooms</li>
                                <li>• Computer Labs (80+ stations)</li>
                                <li>• Executive Boardroom</li>
                            </ul>
                        </div>

                        <div className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-lg transition-all duration-300">
                            <Users className="w-10 h-10 text-indigo-600 mb-4" />
                            <h4 className="text-xl font-bold text-gray-900 mb-4">Accommodation</h4>
                            <ul className="space-y-2 text-gray-700 text-sm">
                                <li>• 10 Deluxe Rooms</li>
                                <li>• 30 Standard Rooms (60 pax)</li>
                                <li>• 200+ capacity Cafeteria</li>
                                <li>• Residential Programs Ready</li>
                            </ul>
                        </div>

                        <div className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-lg transition-all duration-300">
                            <Globe className="w-10 h-10 text-blue-600 mb-4" />
                            <h4 className="text-xl font-bold text-gray-900 mb-4">Infrastructure</h4>
                            <ul className="space-y-2 text-gray-700 text-sm">
                                <li>• 7 Office Spaces</li>
                                <li>• Federated Cloud Services</li>
                                <li>• Testing Labs</li>
                                <li>• Landscaped Campus</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            {/* Partners */}
            {/*<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">*/}
            {/*    <div className="bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-200 rounded-3xl p-12 text-center">*/}
            {/*        <h2 className="text-3xl font-bold text-gray-900 mb-4">Partnership Network</h2>*/}
            {/*        <p className="text-gray-700 mb-8 max-w-3xl mx-auto">*/}
            {/*            Collaborating with universities, government agencies, private sector partners, NGOs, and development organizations across Africa and globally*/}
            {/*        </p>*/}
            {/*        <div className="flex flex-wrap justify-center gap-4 text-sm">*/}
            {/*            <span className="bg-white border border-purple-200 text-gray-700 px-4 py-2 rounded-full">JKUAT</span>*/}
            {/*            <span className="bg-white border border-purple-200 text-gray-700 px-4 py-2 rounded-full">PAUSTI</span>*/}
            {/*            <span className="bg-white border border-purple-200 text-gray-700 px-4 py-2 rounded-full">KALRO</span>*/}
            {/*            <span className="bg-white border border-purple-200 text-gray-700 px-4 py-2 rounded-full">APHRC</span>*/}
            {/*            <span className="bg-white border border-purple-200 text-gray-700 px-4 py-2 rounded-full">CODATA</span>*/}
            {/*            <span className="bg-white border border-purple-200 text-gray-700 px-4 py-2 rounded-full">NRF South Africa</span>*/}
            {/*        </div>*/}
            {/*    </div>*/}
            {/*</div>*/}
            <PartnershipNetwork />

            {/* Conclusion — VC Report §5 */}
            <ConclusionSection />

            {/* Footer */}
            <Footer />
        </div>
    );
}